import { useEffect, useState } from 'react'
import Button from '@/components/button'
import DefaultModal from '@/components/default-modal'
import EmptyState from '@/components/empty-state'
import Spinner from '@/components/spinner'
import { useDefinirPermissoesMutation } from '@/http/mutations/permissoes'
import { usePermissoesCatalogoQuery, usePermissoesUsuarioQuery } from '@/http/queries/permissoes'
import { toErrorMessage } from '@/services/errors'
import type { OperadorResumo } from '@/types/operador'
import type { Acao } from '@/utility/permissions'
import { ACAO_LABEL, agruparAcoesPorCategoria, podeSerConcedidaAOperador } from '@/utility/permissions'
import { orDash } from '@/utility/Utils'
import styles from './style.module.css'

interface OperadorPermissoesModalProps {
  isOpen: boolean
  toggle: () => void
  operador: OperadorResumo | null
}

const OperadorPermissoesModal = ({
  isOpen,
  operador,
  toggle,
}: OperadorPermissoesModalProps) => {
  const [selecionadas, setSelecionadas] = useState<Acao[]>([])

  const usuarioBackofficeId = isOpen ? operador?.id ?? null : null
  const catalogo = usePermissoesCatalogoQuery(isOpen)
  const permissoesDoOperador = usePermissoesUsuarioQuery(usuarioBackofficeId)
  const definirPermissoes = useDefinirPermissoesMutation()

  useEffect(() => {
    if (!isOpen) return

    setSelecionadas((permissoesDoOperador.data ?? []).filter(podeSerConcedidaAOperador))
  }, [isOpen, permissoesDoOperador.data])

  const alternarAcao = (acao: Acao) => {
    setSelecionadas((atual) =>
      atual.includes(acao) ? atual.filter((item) => item !== acao) : [...atual, acao],
    )
  }

  const salvar = () => {
    if (!usuarioBackofficeId) return

    definirPermissoes.mutate(
      { usuarioBackofficeId, acoes: selecionadas },
      { onSuccess: () => toggle() },
    )
  }

  const grupos = agruparAcoesPorCategoria((catalogo.data ?? []).filter(podeSerConcedidaAOperador))
  const carregando = catalogo.isFetching || permissoesDoOperador.isFetching

  return (
    <DefaultModal
      isOpen={isOpen}
      toggle={toggle}
      title={`Permissões de ${orDash(operador?.nome)}`}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={toggle}>
            Cancelar
          </Button>
          <Button
            onClick={salvar}
            isLoading={definirPermissoes.isPending}
            disabled={carregando || permissoesDoOperador.isError}
          >
            Salvar permissões
          </Button>
        </>
      }
    >
      {carregando ? (
        <div className={styles.carregando}>
          <Spinner />
        </div>
      ) : permissoesDoOperador.isError ? (
        <EmptyState
          title="Não foi possível carregar as permissões"
          description={toErrorMessage(permissoesDoOperador.error)}
        />
      ) : (
        <div className={styles.grupos}>
          {grupos.map((grupo) => (
            <div className={styles.grupo} key={grupo.id}>
              <span className={styles.grupoTitulo}>{grupo.titulo}</span>
              <div className={styles.grupoAcoes}>
                {grupo.acoes.map((acao) => (
                  <label className={styles.checkboxLinha} key={acao}>
                    <input
                      type="checkbox"
                      checked={selecionadas.includes(acao)}
                      onChange={() => alternarAcao(acao)}
                    />
                    <span>{ACAO_LABEL[acao] ?? acao}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </DefaultModal>
  )
}

export default OperadorPermissoesModal
